import { isProductionEnvironment } from "@/utils/environments";
import { observer } from "mobx-react";
import { AccountTemplates } from "@/templates/templateListComponents";
import { TemplateType } from "@/templates/templateDomain";
import {
  isTemplateTypeSelected,
  mapTemplateFilterMenuItems,
  projectTemplateFilterMenuItems,
} from "@/templates/templateListDomain";
import { templatePreviewCompanies } from "@/templates/templatePanelDomain";
import { TemplateState } from "@/templates/templateStateDomain";
import { MenuGroup } from "@/components-v2/MenuBar";
import { ReactElement, useEffect, useMemo, useState } from "react";
import {
  useDialogStore,
  useDispatch,
  useLocaleFormat,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import {
  AccountSettingsTab,
  AddAdministratorAccessModel,
  AddCompaniesModel,
  calcColCount,
  CheckAvailabilityResponse,
  EnterpriseAccountChangeType,
  EnterpriseAccountsStore,
  formatAccountStatus,
  formatLink,
  isEnterpriseAccountsTab,
  NewPortfolioOption,
  openAccountCompany,
  productNameLabel,
  RemoveAdministratorAccessModel,
  subscriptionRestriction,
} from "./enterpriseAccountsDomain";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Box, Card, Container, Grid, GridItem, VBox } from "@/components-v2/Box";
import {
  AccountCompanyDetails,
  billingFrequencyLabels,
  CompanyItem,
  EnterpriseAccountDetails,
  EnterpriseAccountStatus,
  EnterpriseAccountUser,
  EventLogItem,
  isFailure,
  isOk,
  isPending,
} from "@/common/common";
import { Icon } from "@/components-v2/Icon";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { confirmAction, loadUrl, navigate, thunk } from "@/events/eventsDomain";
import {
  navigationReferrerUrl,
  portfolioUrl,
  superadminSearchUrl,
  superadminUrl,
} from "@/utils/navigation";
import { InfoTag } from "@/components-v2/InfoTag";
import { TabProps, Tabs } from "@/components-v2/Tabs";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { formatOrgNum } from "@/utils/other";
import { MTextInput } from "@/components-v2/TextInput";
import { MSelect, Select } from "@/components-v2/Select";
import { Dialog } from "@/components-v2/Dialog";
import { MTextArea } from "@/components-v2/TextArea";
import { AccountSettingsLeftPanel, Settings } from "./integrationsComponents";
import { AccountFieldsSection } from "@/customFields/accountFieldsComponents";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { Table } from "@/components-v2/Table";
import { getFullName } from "@/utils/Roles";
import { identity } from "lodash";
import { isEnter } from "@/utils/keyboardEvents";
import { Checkbox, MCheckbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { HoverTooltip } from "@/components-v2/Tooltip";
import { IconName } from "@/components-v2/iconMapping";
import { TFunction, TMessage } from "@/i18n/tt";
import { toDate, toLocalDateISOString } from "@/utils/Dates";
import { AddCompanyDialog } from "@/addCompany/addCompanyComponents";
import { openAddCompany } from "@/addCompany/addCompanyDomain";
import { CompanyImportDialog } from "@/companyImport/companyImportComponents";
import { companyImportTemplateUrl, openCompanyImport } from "@/companyImport/companyImportDomain";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { ProductDescription } from "@/common/product";
import { Language } from "@/i18n/languages";
import { TMessageKey } from "@/i18n/types.gen";
import { Alert } from "@/components-v2/Alert";
import { Markdown } from "@/text/Markdown";
import { Tag } from "@/components-v2/Tag";
import compact from "lodash/compact";
import { isReactElement } from "@/utils/reactHelpers";
import { LastLogin } from "@/common/commonComponents.tsx";
import { isMobileView } from "@/utils/screen.ts";
import { InviteNewAdminDialog } from "@/portfolio/contacts/contactsComponents.tsx";
import { InviteUserToCustomRoleDialog } from "@/portfolio/contacts/customRoleComponents.tsx";
import { DataCard, DataCardProps } from "@/components-v2/DataCard.tsx";

const companyImportFileConfig: FileUploadConfig = {
  supportedFileTypes: [{ extensions: ["xlsx"], magicBytes: [] }],
  maxFileSize: 10 * 1024 * 1024,
};

export const EnterpriseAccountPage = observer(function EnterpriseAccountPage({
  id,
  tab,
  accountSettingsTab,
  templateFilter,
  field,
  templateId,
}: {
  id: string;
  tab?: string;
  accountSettingsTab?: string;
  templateFilter?: string;
  field?: string;
  templateId?: string;
}) {
  const { http, messageStore, dispatch, userStore, templates } = useRootStore();
  const [store] = useState(
    () => new EnterpriseAccountsStore(http, messageStore, dispatch, userStore),
  );
  const { t } = useTranslation();

  useEffect(() => {
    store.fetchEnterpriseAccount(id);
    return store.reset;
  }, [store, id]);

  useEffect(() => {
    store.setActiveTab(isEnterpriseAccountsTab(tab) ? tab : "companies");
  }, [store, tab]);

  const activeSettingsTab = store.activeAccountSettingsTab(accountSettingsTab);

  useEffect(() => {
    if (tab === "settings" && isOk(store.accountResult)) {
      store.fetchDataForTab(activeSettingsTab);
    }
  }, [store, tab, activeSettingsTab, store.accountResult]);

  switch (true) {
    case isPending(store.accountResult):
      return (
        <VBox fullheight justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    case isOk(store.accountResult):
      return (
        <EnterpriseAccountView
          data={store.accountResult.data}
          store={store}
          messageStore={messageStore}
          accountSettingsTab={activeSettingsTab}
          templateTypeSegment={accountSettingsTab}
          templateFilterSegment={templateFilter}
          editFieldId={field}
          templateId={templateId}
          templates={templates}
        />
      );
    case isFailure(store.accountResult):
      return (
        <VBox fullheight justifyContent={"center"} alignItems={"center"}>
          <Icon name={"warning-circle"} color={"error"} size={"jumbo"} />
          <Box textSize={"lg"} fg={"error"}>
            {t("error.fetch.data")}
          </Box>
        </VBox>
      );
    default:
      return null;
  }
});

const ConfirmRemoveCompanyDialog = observer(function ConfirmRemoveCompanyDialog({
  store,
  data,
}: {
  store: EnterpriseAccountsStore;
  data: EnterpriseAccountDetails;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      onCancel={store.closeDialog}
      onSubmit={store.submitRemoveCompanyDialog}
      title={{ key: "enterprise-account.confirm-remove.title" }}
      maxWidth={"sm"}
    >
      <Markdown
        value={t("enterprise-account.confirm-remove-company", {
          companyName: store.selectedCompanyDetails?.companyName,
          accountName: data.account.name,
        })}
        enableLinks
      />
      <VBox>
        <p>
          {t("enterprise-account.offboard-company.header", {
            companyName: store.selectedCompanyDetails?.companyName,
          })}
        </p>
        <Checkbox
          label={{ key: "enterprise-account.offboard-company.label" }}
          checked={store.offboardSelectedCompany}
          onChange={(e) => store.setOffboardSelectedCompany(e.target.checked)}
        />
        {store.offboardSelectedCompany && (
          <Alert
            message={{ key: "enterprise-account.offboard-company.warning" }}
            type={"error"}
            icon={"warning-circle"}
          />
        )}
      </VBox>
    </Dialog>
  );
});

export const GrantProjectAccessDialog = ({
  user,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  user: EnterpriseAccountUser;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{ key: "enterprise-account.grant-project-access.title", params: { name: user.name } }}
      maxWidth={"sm"}
      submitLabel={{ key: "enterprise-account.grant-project-access.submit" }}
      cancelLabel={t("cancel")}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
    >
      <p>{t("enterprise-account.grant-project-access.description", { name: user.name })}</p>
    </Dialog>
  );
};

export const EditAdministratorAccessDialog = observer(
  ({
    title,
    model,
    onSubmit,
    onCancel,
    submitLabel,
    isSubmitting,
  }: {
    title: TMessage;
    model: AddAdministratorAccessModel | RemoveAdministratorAccessModel;
    onSubmit: (externalId: string, organisationNumbers: string[]) => void;
    onCancel: () => void;
    submitLabel: TMessage;
    isSubmitting?: boolean;
  }) => {
    const { t } = useTranslation();

    return (
      <Dialog
        title={title}
        maxWidth={"sm"}
        submitLabel={submitLabel}
        cancelLabel={t("cancel")}
        onSubmit={() =>
          onSubmit(
            model.selectedAdmin.id,
            model.availableCompanies.selected.map((a) => a.organisationNumber),
          )
        }
        onCancel={onCancel}
        isSubmitDisabled={model.isSubmitDisabled}
        isSubmitting={isSubmitting}
      >
        <VBox gap={"md"}>
          <Box>
            <LinkButton
              label={t("select-all")}
              onClick={() => model.availableCompanies.selectAll()}
            />
            {"/"}
            <LinkButton label={t("none")} onClick={() => model.availableCompanies.clear()} />
          </Box>
          <MCheckboxGroup
            model={model.availableCompanies}
            labelFn={(item) => (
              <Box>
                <div>{item.companyName}</div>
                <small className={"subtle"}>{item.organisationNumber}</small>
              </Box>
            )}
          />
        </VBox>
      </Dialog>
    );
  },
);

export const PurgeAccountDialog = observer(({ store }: { store: EnterpriseAccountsStore }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("enterprise-account.purge-account-title")}
      cancelLabel={t("close")}
      onCancel={store.closeDialog}
      onSubmit={store.purgeAccount}
      submitLabel={t("confirm")}
      isSubmitDisabled={store.isPurgeAccountDisabled}
      isSubmitting={isPending(store.purgeAccountResult)}
    >
      <VBox gap={"md"}>
        <div>{t("enterprise-account.purge-account-description")}</div>
        {store.confirmPortfolioDeletionModel && store.accountPortfolios.length > 0 && (
          <MCheckbox
            model={store.confirmPortfolioDeletionModel}
            label={t(store.confirmPortfolioDeletionLabel)}
          />
        )}
      </VBox>
    </Dialog>
  );
});

const EnterpriseAccountView = observer(function EnterpriseAccountView({
  data,
  store,
  messageStore,
  accountSettingsTab,
  templateTypeSegment,
  templateFilterSegment,
  editFieldId,
  templateId,
  templates,
}: {
  data: EnterpriseAccountDetails;
  store: EnterpriseAccountsStore;
  messageStore: AlertMessageStore;
  accountSettingsTab: AccountSettingsTab;
  templateTypeSegment?: string;
  templateFilterSegment?: string;
  editFieldId?: string;
  templateId?: string;
  templates?: TemplateState;
}) {
  const { t } = useTranslation();
  const previewCompanies = useMemo(
    () => templatePreviewCompanies(data.companyDetails, data.account.features),
    [data.companyDetails, data.account.features],
  );

  const renderLeftPanel = () => {
    switch (store.activeTab) {
      case "settings":
        return <AccountSettingsLeftPanel store={store} accountSettingsTab={accountSettingsTab} />;
      case "content":
        return templates && <ContentLeftPanel templates={templates} />;
      default:
        return undefined;
    }
  };

  const render = () => {
    switch (store.activeTab) {
      case "companies":
        return data.companyDetails.length === 0 ? (
          <EmptyState store={store} />
        ) : (
          <CompaniesTable store={store} />
        );
      case "account":
        return <AccountView store={store} data={data} />;
      case "users":
        return <UserView store={store} data={data} />;
      case "fields":
        return store.hasEntityDataFeature ? (
          <AccountFieldsSection
            accountId={data.account.id}
            accountName={data.account.name}
            companies={data.companyDetails}
            editDefinitionId={editFieldId}
          />
        ) : null;
      case "settings":
        return <Settings store={store} accountSettingsTab={accountSettingsTab} data={data} />;
      case "content":
        return (
          <AccountTemplates
            account={{ id: data.account.id, name: store.accountName }}
            companies={previewCompanies}
            typeSegment={templateTypeSegment}
            filterSegment={templateFilterSegment}
            templateId={templateId}
            templates={templates}
          />
        );
    }
  };

  const renderDialogs = () => {
    switch (store.showDialog.type) {
      case "remove-all-companies":
        return (
          <Dialog
            title={{ key: "enterprise-account.remove-all-companies" }}
            onSubmit={store.removeAllCompanies}
            onCancel={store.closeDialog}
            submitLabel={{ key: "enterprise-account.remove-all-companies" }}
            submitColor={"error"}
          >
            {t("enterprise-account.confirm-remove-all-companies")}
          </Dialog>
        );
      case "delete-account":
        return (
          <Dialog
            title={{
              key: "enterprise-account.delete-account",
              params: { name: data.account.name },
            }}
            onSubmit={store.deleteAccount}
            onCancel={store.closeDialog}
            submitLabel={{
              key: "enterprise-account.delete-account",
              params: { name: data.account.name },
            }}
            submitColor={"error"}
          >
            {t("enterprise-account.confirm-delete-account")}
          </Dialog>
        );
      case "find-companies":
        return <FindCompaniesDialog store={store} />;
      case "update-companies":
        return <UpdateMultipleCompaniesDialog store={store} />;
      case "confirm-remove":
        return <ConfirmRemoveCompanyDialog store={store} data={data} />;
      case "add-admin-access":
        return (
          <EditAdministratorAccessDialog
            title={store.titleAddCompanies(store.showDialog.model.selectedAdmin)}
            submitLabel={{ key: "add" }}
            model={store.showDialog.model}
            onCancel={store.closeDialog}
            onSubmit={store.submitAddAdministratorAccessDialog}
            isSubmitting={isPending(store.submitCompaniesResult)}
          />
        );
      case "remove-admin-access":
        return (
          <EditAdministratorAccessDialog
            title={store.titleRemoveCompanies(store.showDialog.model.selectedAdmin)}
            submitLabel={{ key: "remove" }}
            model={store.showDialog.model}
            onCancel={store.closeDialog}
            onSubmit={store.submitRemoveAdministratorAccessDialog}
            isSubmitting={isPending(store.submitCompaniesResult)}
          />
        );
      case "grant-project-access":
        return (
          <GrantProjectAccessDialog
            user={store.showDialog.user}
            onSubmit={store.submitGrantProjectAccess}
            onCancel={store.closeDialog}
            isSubmitting={isPending(store.submitDialogResult)}
          />
        );
      case "purge-account":
        return <PurgeAccountDialog store={store} />;

      case "invite-admin":
        return <InviteNewAdminDialog store={store.showDialog.model} />;
      case "invite-custom-role":
        return <InviteUserToCustomRoleDialog model={store.showDialog.model} />;
      case "none":
        return undefined;
    }
  };
  return (
    <FullScreenLayout
      alertStore={messageStore}
      pageHeader={<PageHeader store={store} data={data} tabs={store.tabs} />}
      leftPanel={renderLeftPanel()}
      leftPanelContainerProps={{ htmlProps: { style: { flex: "0 0 300px" } } }}
      centerPanelContainerProps={store.activeTab === "fields" ? { overflow: "hidden" } : undefined}
      centerPanel={
        <>
          {render()}
          <AddCompanyDialog />
          <CompanyImportDialog />
          {store.addCompaniesModel && (
            <AddCompaniesDialog
              model={store.addCompaniesModel}
              onCancel={store.closeAddCompaniesDialog}
              onSubmit={store.submitAddCompaniesDialog}
              isSubmitting={isPending(store.submitCompaniesResult)}
            />
          )}
          {renderDialogs()}
        </>
      }
    />
  );
});

interface EmptyStateProps {
  store: EnterpriseAccountsStore;
}

const EmptyState = observer(function EmptyState({ store }: EmptyStateProps) {
  const { t } = useTranslation();
  return (
    <Container
      marginTop={"md"}
      maxWidth={"xl"}
      justifyContent={"center"}
      alignItems={"center"}
      fullheight
      gap={"lg"}
    >
      <h3>{t("enterprise-account.no-companies")}</h3>
      {store.hasSuperAdminAccess && (
        <Button
          variant={"secondary"}
          onClick={store.openAddCompaniesDialog}
          icon={"plus"}
          label={{ key: "enterprise-account.add-companies" }}
        />
      )}
    </Container>
  );
});

const PageHeader = observer(function PageHeader({
  data,
  tabs,
  store,
}: {
  data: EnterpriseAccountDetails;
  tabs: TabProps[];
  store: EnterpriseAccountsStore;
}) {
  const { t, lang } = useTranslation();
  const dialogStore = useDialogStore();
  const { formatDate } = useLocaleFormat();
  const location = useRootStore().location;
  const hubspotLink = data.account.metadata.hubspotLink;
  const tripletexLink = data.account.metadata.tripletexLink;
  const keyAccountManager = data.account.metadata.keyAccountManager;
  return (
    <VBox bg={"default"} borderBottom>
      <Box grow alignItems={"center"} paddingLeft={"md"} paddingRight={"lg"} paddingY={"md"}>
        <IconButton
          icon={"x"}
          tooltip={{ key: "back" }}
          onClick={[
            navigate(
              navigationReferrerUrl(location.state) ??
                (store.hasSuperAdminAccess
                  ? `${superadminUrl}/enterprise-accounts`
                  : `${portfolioUrl}/all/companies`),
            ),
          ]}
        />
        {t(
          store.hasSuperAdminAccess
            ? "enterprise-account.manage"
            : "enterprise-account.details.header",
        )}
      </Box>
      <Container maxWidth={"xl"}>
        <Box>
          <VBox gap={"md"}>
            <h3>{data.account.name}</h3>
            <Box>
              {store.hasSuperAdminAccess &&
                isOk(store.accountResult) &&
                store.accountResult.data.account.internalReferenceName && (
                  <InfoTag
                    id={"tag"}
                    icon={"tag"}
                    text={store.accountResult.data.account.internalReferenceName}
                  />
                )}
              <InfoTag
                id={"status"}
                icon={
                  data.account.status === EnterpriseAccountStatus.ACTIVE
                    ? { name: "circle", color: "success", weight: "fill", size: "xs" }
                    : "circle-notch"
                }
                text={{
                  key:
                    data.account.status === EnterpriseAccountStatus.ACTIVE
                      ? "enterprise-account.status.active"
                      : "enterprise-account.status.inactive",
                }}
              />
              <InfoTag
                id={"startTime"}
                icon={"handshake"}
                text={formatDate(data.account.startTime)}
              />
              {data.subscriptionStats.map((s) => (
                <InfoTag
                  key={s.productId}
                  id={s.productId}
                  tooltip={{
                    translated: productLanguage(data.products, s.productId, lang)?.name ?? "",
                  }}
                  icon={"notebook"}
                  text={formatAccountStatus(lang, data, s.productId)}
                />
              ))}
              {keyAccountManager && store.hasSuperAdminAccess && (
                <InfoTag
                  id={"keyAccountManager"}
                  tooltip={{ key: "enterprise-account.key-account-manager" }}
                  text={keyAccountManager}
                  icon={"user-square"}
                />
              )}
              {hubspotLink && store.hasSuperAdminAccess && (
                <Button
                  variant={"secondary"}
                  icon={"arrow-square-out"}
                  onClick={() => window.open(formatLink(hubspotLink))}
                  label={"Hubspot"}
                  size={"xs"}
                />
              )}
              {tripletexLink && store.hasSuperAdminAccess && (
                <Button
                  variant={"secondary"}
                  icon={"arrow-square-out"}
                  onClick={() => window.open(formatLink(tripletexLink))}
                  label={"Tripletex"}
                  size={"xs"}
                />
              )}
            </Box>
          </VBox>
          <Box grow justifyContent={"end"} alignItems={"center"}>
            {store.hasSuperAdminAccess && (
              <Button
                variant={"secondary"}
                label={{ key: "enterprise-account.edit-details" }}
                icon={"edit"}
                onClick={() =>
                  dialogStore.openEnterpriseAccountDialog(store.refresh, data, data.products)
                }
              />
            )}
          </Box>
        </Box>
        <Tabs tabs={tabs} scrollable={false} />
      </Container>
    </VBox>
  );
});

function labelFn(
  lang: Language,
  store: EnterpriseAccountsStore,
  fallbackMessageKey?: TMessageKey,
): (s: string | null) => TMessage {
  return (s: string | null): TMessage => {
    const productLanguage = store.getProductLanguage(s, lang);
    return productLanguage
      ? { translated: productLanguage?.name ?? "" }
      : fallbackMessageKey
        ? { key: fallbackMessageKey }
        : { translated: " " };
  };
}

export const CompaniesTable = observer(function CompaniesTable({
  store,
}: {
  store: EnterpriseAccountsStore;
}) {
  const dispatch = useDispatch();
  const { formatNumber } = useLocaleFormat();
  const columns: TableColumn<AccountCompanyDetails>[] = [
    {
      name: "companyName",
      label: { key: "name" },
      align: "left",
      getValue: (a) => (
        <VBox>
          <span>{a.companyName}</span>
          {a.portfolioName && (
            <Box className={"subtle"} textSize={"sm"}>
              {a.portfolioName}
            </Box>
          )}
        </VBox>
      ),
      getSortValue: (row) => row.companyName,
    },
    {
      name: "orgNo",
      label: { key: "org-no" },
      align: "left",
      getValue: (row) => formatOrgNum(row.orgNo),
    },
    {
      name: "shareholderCount",
      label: { key: "enterprise-account.number-of-shareholders" },
      getValue: (row) => formatNumber(row.shareholderCount),
      getSortValue: (row) => row.shareholderCount,
    },
    {
      name: "productId",
      label: { key: "subscription" },
      align: "left",
      getValue: (row) => (
        <Select
          value={row.productId ?? null}
          items={[...store.productIds, null]}
          onSelect={(value) => {
            store.changeSubscriptionForCompany(row, value);
          }}
          idFn={identity}
          labelFn={labelFn(lang, store, "none")}
          disabledReasonFn={(productId) =>
            subscriptionRestriction(store.products, productId, [row])
          }
          disabled={!store.canUpdateSubscriptions}
        />
      ),
    },
  ];

  const { lang } = useTranslation();

  return (
    <Container marginTop={"md"} maxWidth={"xl"}>
      {store.hasSuperAdminAccess && <BillingCycleCards store={store} />}
      <Box marginTop={"md"}>
        <MTextInput
          model={store.nameFilter}
          placeholder={{ key: "search" }}
          icon={"magnifying-glass"}
          type={"search"}
          inline
        />
        {store.subscriptionTypeFilter && (
          <Box>
            <MSelect
              model={store.subscriptionTypeFilter}
              labelFn={labelFn(lang, store, "enterprise-account.subscriptionFilter-all")}
            />
          </Box>
        )}
        {store.portfolioFilter && (
          <Box>
            <MSelect model={store.portfolioFilter} />
          </Box>
        )}
        {store.hasSuperAdminAccess && (
          <Box grow justifyContent={"end"} gap={"xl"}>
            <DropdownMenu
              button={{ type: "link", label: { key: "enterprise-account.tools" } }}
              items={[
                {
                  type: "item",
                  label: { key: "enterprise-account.find-companies" },
                  icon: "magnifying-glass",
                  onSelect: store.openFindCompaniesDialog,
                },
                {
                  type: "item",
                  label: { key: "enterprise-account.update-multiple-companies" },
                  icon: "edit",
                  onSelect: store.openUpdateMultipleCompaniesDialog,
                },
                {
                  type: "item",
                  label: { key: "general.update-companies-title-case" },
                  icon: "paint-brush-broad",
                  onSelect: confirmAction({
                    title: { key: "general.update-companies-title-case" },
                    text: {
                      key: "enterprise-account.update-companies-title-case-description",
                      params: {
                        count: store.companyData.length,
                      },
                    },
                    confirmLabel: { key: "continue" },
                    cancelLabel: { key: "cancel" },
                    onConfirm: [thunk(() => store.updateCompaniesTitleCase())],
                  }),
                },
              ]}
            />
            <Button
              variant={"secondary"}
              onClick={store.openAddCompaniesDialog}
              icon={"plus"}
              label={{ key: "enterprise-account.add-companies" }}
            />
          </Box>
        )}
        {store.canAddCompany && !store.hasSuperAdminAccess && (
          <Box grow justifyContent={"end"} gap={"xl"}>
            <AddCompanyButton store={store} />
          </Box>
        )}
        {store.canAddCompany && store.hasSuperAdminAccess && <AddCompanyButton store={store} />}
      </Box>
      <DataTable
        tableColumns={columns}
        onRowClick={(row) => dispatch(openAccountCompany(row.orgNo, store.hasSuperAdminAccess))}
        tableData={store.companyData}
        getId={(row) => row.orgNo}
        rowActions={(a) =>
          store.hasSuperAdminAccess
            ? [
                [
                  "context-menu",
                  [
                    {
                      type: "item",
                      label: { key: "enterprise-accounts.remove-from-account" },
                      icon: "trash",
                      onSelect: () => store.openConfirmRemoveDialog(a),
                    },
                  ],
                ],
              ]
            : []
        }
        initialSortBy={"companyName"}
        initialSortDirection={"asc"}
      />
    </Container>
  );
});

const AddCompaniesDialog = observer(function AddCompaniesDialog({
  model,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  model: AddCompaniesModel;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{ key: "enterprise-account.add-companies" }}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      maxWidth={"sm"}
      isSubmitDisabled={!model.isAvailabilityCheckOk}
    >
      <VBox gap={"lg"}>
        <Box alignItems={"end"}>
          <MTextInput
            model={model.searchField}
            placeholder={{ key: "enterprise-account.search-placeholder" }}
            icon={"magnifying-glass"}
            type={"search"}
            onKeyDown={(e) => {
              if (isEnter(e)) {
                e.preventDefault();
                model.search();
              }
            }}
          />
          <Button
            variant={"primary"}
            onClick={model.search}
            icon={"magnifying-glass"}
            loading={isPending(model.searchUserResult)}
            tooltip={{ key: "search" }}
          />
        </Box>
        {isOk(model.searchUserResult) && (
          <VBox>
            <Box>
              {t("enterprise-account.users-found", { count: model.searchUserResult.data.length })}
            </Box>
            <VBox>
              {model.searchUserResult.data.map((d) => (
                <Box key={d.externalId}>
                  {d.userId}{" "}
                  <LinkButton
                    label={d.name}
                    onClick={() => model.fetchUserCompanies(d.externalId)}
                  />
                  ({d.email})
                </Box>
              ))}
            </VBox>
          </VBox>
        )}
        <MTextArea model={model.orgNos} placeholder={t("enterprise-account.paste-orgnos")} />
        <Button
          variant={"secondary"}
          label={t("enterprise-account.check-availability")}
          onClick={model.checkAvailability}
          loading={isPending(model.checkAvailabilityResult)}
        />
        {isOk(model.checkAvailabilityResult) && (
          <AvailabilityAlert data={model.checkAvailabilityResult.data} />
        )}
        {model.isAvailabilityCheckOk && (
          <>
            {model.adminUsers && (
              <VBox>
                <Box>{t("enterprise-account.add-admins")}</Box>
                <MCheckboxGroup
                  model={model.adminUsers}
                  labelFn={(admin) => (
                    <Box>
                      {t(admin.name)}{" "}
                      {model.isAccountAdmin(admin.id) && (
                        <Tag
                          label={{ key: "enterprise-account.account-admin" }}
                          color={"neutral"}
                        />
                      )}
                    </Box>
                  )}
                />
                {model.adminUsers.options.length === 1 && (
                  <Box fg={"subtle"}>{t("enterprise-account.no-other-admins-found")}</Box>
                )}
              </VBox>
            )}
            <MSelect model={model.subscriptionModel} label={t("enterprise-account.subscription")} />
            <Checkbox
              label={{ key: "enterprise-account.complete-onboarding" }}
              checked={model.completeOnboarding.value}
              onChange={(e) => model.completeOnboarding.setValue(e.target.checked)}
            />
            {isFailure(model.fetchPortfoliosResult) && (
              <Alert message={{ key: "error.fetch.data" }} type={"error"} />
            )}
            <VBox>
              <MSelect
                model={model.portfolioModel}
                label={t("enterprise-account.add-companies-to-portfolio")}
                labelFn={(portfolio) =>
                  portfolio ? (
                    <Box alignItems={"center"}>
                      {portfolio === NewPortfolioOption ? (
                        <>
                          <Icon name={"plus"} /> {t("enterprise-account.create-portfolio")}
                        </>
                      ) : (
                        <>
                          <Icon name={"briefcase"} /> {portfolio.name}
                        </>
                      )}
                    </Box>
                  ) : (
                    " "
                  )
                }
              />
              {model.portfolioModel.value === NewPortfolioOption && (
                <Dialog
                  title={{ key: "enterprise-account.create-portfolio" }}
                  maxWidth={"xs"}
                  onSubmit={model.createNewPortfolio}
                  onCancel={model.closeNewPortfolioDialog}
                  submitResult={model.createPortfolioResult}
                >
                  <MTextInput model={model.newPortfolioName} />
                </Dialog>
              )}
            </VBox>
          </>
        )}{" "}
      </VBox>
    </Dialog>
  );
});

const AvailabilityAlert = observer(function AvailabilityAlert({
  data,
}: {
  data: CheckAvailabilityResponse;
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  if (
    data.invalidOrgNos.length === 0 &&
    data.notFoundOrgNos.length === 0 &&
    data.companiesInOtherAccount.length === 0
  ) {
    return (
      <Alert
        message={{
          key: "enterprise-account.check-availability.success",
          params: { count: data.availableOrgNos.length },
        }}
        type={"success"}
      />
    );
  }
  return (
    <Alert
      message={
        <VBox>
          <Markdown value={t("enterprise-account.check-availability.error")} enableLinks />
          {data.companiesInOtherAccount.map((c) => (
            <Markdown
              key={c.orgNo}
              value={t("enterprise-account.check-availability.inOtherAccount", {
                companyName: c.companyName,
                orgNo: c.orgNo,
                link: superadminSearchUrl(c.orgNo),
                enterpriseAccountName: c.enterpriseAccountName,
              })}
              onActionClick={() => dispatch([loadUrl(superadminSearchUrl(c.orgNo))])}
              inheritLinkColor
              enableLinks
            />
          ))}
          {data.notFoundOrgNos.map((orgNo) => (
            <div key={orgNo}>{t("enterprise-account.check-availability.notFound", { orgNo })}</div>
          ))}
          {data.invalidOrgNos.map((orgNo) => (
            <div key={orgNo}>{t("enterprise-account.check-availability.invalid", { orgNo })}</div>
          ))}
        </VBox>
      }
      type={"error"}
    />
  );
});

const FindCompaniesDialog = observer(function FindCompaniesDialog({
  store,
}: {
  store: EnterpriseAccountsStore;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Dialog
      title={{ key: "enterprise-account.found-companies.title" }}
      onCancel={store.closeDialog}
      onSubmit={store.addFoundCompanies}
      maxWidth={"sm"}
      isSubmitting={isPending(store.submitCompaniesResult)}
      submitLabel={{
        key: "enterprise-account.add-to",
        params: {
          count: store.filteredCheckedCompanies.length,
          name: isOk(store.accountResult) ? store.accountResult.data.account.name : undefined,
        },
      }}
      isSubmitDisabled={store.checkedOrgNos.size === 0}
    >
      {isPending(store.findCompaniesResult) && <LoadingIndicator />}
      {isOk(store.findCompaniesResult) && (
        <VBox gap={"md"}>
          <Box>
            {t("enterprise-account.found-companies.info", {
              count: store.findCompaniesResult.data.length,
              name: isOk(store.accountResult) ? store.accountResult.data.account.name : undefined,
            })}
          </Box>
          <Box justifyContent={"space-between"}>
            <Box alignItems={"center"}>
              <LinkButton label={{ key: "select-all" }} onClick={store.selectAllCompanies} /> /
              <LinkButton label={{ key: "none" }} onClick={store.selectNoCompanies} />
            </Box>

            {store.adminUserFilter && (
              <Box>
                <MSelect model={store.adminUserFilter} />
              </Box>
            )}
          </Box>
          <Table variant={"IN_CARD"}>
            <thead>
              <tr>
                <td>{t("enterprise-account.company")}</td>
                <td>{t("common.admin")}</td>
              </tr>
            </thead>
            <tbody>
              {store.filteredCompanies.map((c) => (
                <tr key={c.companyData.orgNo}>
                  <td>
                    <Box>
                      <Checkbox
                        checked={store.isCompanyChecked(c.companyData.orgNo)}
                        onChange={() => store.toggleCompany(c.companyData.orgNo)}
                        id={c.companyData.orgNo}
                      />
                      <VBox gap={"xs"}>
                        <label htmlFor={c.companyData.orgNo}>{c.companyData.name}</label>
                        <small>{t(productNameLabel(c.productName))}</small>
                      </VBox>
                    </Box>
                  </td>
                  <td>
                    {c.admins.length === 1 ? (
                      c.admins[0].name
                    ) : (
                      <HoverTooltip
                        content={
                          <VBox>
                            {c.admins.map((a) => (
                              <div key={a.name}>{a.name}</div>
                            ))}
                          </VBox>
                        }
                      >
                        {({ targetRef }) => (
                          <span ref={targetRef} className={"underline decoration-dotted"}>
                            {c.admins.length}
                          </span>
                        )}
                      </HoverTooltip>
                    )}
                  </td>
                  <td width={1}>
                    <IconButton
                      icon={"arrow-square-out"}
                      tooltip={{ key: "enterprise-account.open-company" }}
                      onClick={() => dispatch([loadUrl(superadminSearchUrl(c.companyData.orgNo))])}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </VBox>
      )}
    </Dialog>
  );
});

const UpdateMultipleCompaniesDialog = observer(function UpdateMultipleCompaniesDialog({
  store,
}: {
  store: EnterpriseAccountsStore;
}) {
  const { t, lang } = useTranslation();
  const model = store.updateMultipleCompaniesModel;
  if (!model) return null;
  return (
    <Dialog
      title={{ key: "enterprise-account.update-multiple-companies" }}
      onCancel={store.closeDialog}
      maxWidth={"sm"}
      isSubmitDisabled={model.isSubmitDisabled}
      onSubmit={store.submitUpdateMultipleCompaniesDialog}
    >
      <VBox>
        {t("enterprise-account.choose-companies")}
        <Box justifyContent={"space-between"}>
          <Box alignItems={"center"}>
            <LinkButton label={{ key: "select-all" }} onClick={model.selectAll} /> /
            <LinkButton label={{ key: "none" }} onClick={model.unselectAll} />
          </Box>
          <Box>
            <MSelect
              model={model.productFilter}
              labelFn={labelFn(lang, store, "enterprise-account.subscriptionFilter-all")}
            />
          </Box>
        </Box>
        {model.companies.options.map((c) => (
          <Box key={c.orgNo} justifyContent={"space-between"}>
            <Checkbox
              label={c.companyName}
              checked={model.companies.isSelected(c)}
              onChange={() => model.companies.toggle(c)}
            />
            <div>{model.productName(c.productId, lang)}</div>
          </Box>
        ))}
        <MSelect model={model.changeType} label={{ key: "enterprise-account.choose-change" }} />
        {model.changeType.value === EnterpriseAccountChangeType.subscription && (
          <>
            <MSelect
              model={model.subscriptionModel}
              label={{ key: "enterprise-account.update-subscription-to" }}
              disabled={model.companies.options.length === 0}
              labelFn={labelFn(lang, store, "none")}
              disabledReasonFn={(productId) =>
                subscriptionRestriction(model.account.products, productId, model.companies.selected)
              }
            />
            {model.selectedSubscriptionRestriction && (
              <Alert message={model.selectedSubscriptionRestriction} type={"warning"} />
            )}
          </>
        )}
        {model.changeType.value === EnterpriseAccountChangeType.removeCompanies && (
          <>
            {t("enterprise-account.offboard-companies.header")}
            <Checkbox
              label={{ key: "enterprise-account.offboard-companies.label" }}
              checked={model.offboardModel.value}
              onChange={(e) => model.offboardModel.setValue(e.target.checked)}
            />
            {model.offboardModel.value && (
              <Alert
                type={"error"}
                icon={"warning"}
                message={{ key: "enterprise-account.offboard-companies.warning" }}
              />
            )}
          </>
        )}
        {model.changeType.value === EnterpriseAccountChangeType.addToPortfolio && (
          <VBox gap marginTop>
            <div>{t("enterprise-account.portfolio-update-to")}</div>
            <MSelect
              model={model.addToPortfolioModel}
              placeholder={
                model.portfolioConfig.defaultLabel ??
                t("enterprise-account.portfolio-select-placeholder")
              }
            />
            {model.addToPortfolioModel.value === NewPortfolioOption && (
              <MTextInput
                model={model.portfolioNewNameModel}
                label={t("enterprise-account.portfolio-name")}
              />
            )}
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});

const AccountView = observer(function AccountView({
  data,
  store,
}: {
  data: EnterpriseAccountDetails;
  store: EnterpriseAccountsStore;
}) {
  return (
    <Container marginTop={"md"} maxWidth={"xl"} gap={"md"}>
      <AccountDetails data={data} hasSuperAdminAccess={store.hasSuperAdminAccess} />
      <ProductStatus data={data} />
      {store.hasSuperAdminAccess && <AccountHistory data={data} />}
    </Container>
  );
});

const AccountDetails = observer(function AccountDetails({
  data,
  hasSuperAdminAccess,
}: {
  data: EnterpriseAccountDetails;
  hasSuperAdminAccess: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card title={{ key: "enterprise-account.details" }}>
      <Table variant={"IN_CARD"}>
        <colgroup>
          <col className={"w-1/2"} />
        </colgroup>
        <tbody>
          <tr>
            <td>{t("enterprise-account.account-name")}</td>
            <td>{data.account.name}</td>
          </tr>
          {hasSuperAdminAccess && data.account.internalReferenceName && (
            <tr>
              <td>{t("enterprise-account.internal-reference-name")}</td>
              <td>{data.account.internalReferenceName}</td>
            </tr>
          )}
          <tr>
            <td>{t("enterprise-account.contact-name")}</td>
            <td>{getFullName(data.account.personInfo)}</td>
          </tr>
          <tr>
            <td>{t("enterprise-account.contact-email")}</td>
            <td>
              {data.account.personInfo.email && (
                <a href={`mailto:${data.account.personInfo.email}`}>
                  {data.account.personInfo.email}
                </a>
              )}
            </td>
          </tr>
          <tr>
            <td>{t("enterprise-account.primary-company")}</td>
            <td>{data.account.primaryCompanyOrgNo}</td>
          </tr>
          {data.account.metadata.keyAccountManager && hasSuperAdminAccess && (
            <tr>
              <td>{t("enterprise-account.key-account-manager")}</td>
              <td>{data.account.metadata.keyAccountManager}</td>
            </tr>
          )}
          {data.account.metadata.hubspotLink && hasSuperAdminAccess && (
            <tr>
              <td>{t("enterprise-account.hubspot")}</td>
              <td>
                <a href={formatLink(data.account.metadata.hubspotLink)} target={"_blank"}>
                  {formatLink(data.account.metadata.hubspotLink)}
                </a>
              </td>
            </tr>
          )}
          {data.account.metadata.tripletexLink && hasSuperAdminAccess && (
            <tr>
              <td>{t("enterprise-account.tripletex")}</td>
              <td>
                <a href={formatLink(data.account.metadata.tripletexLink)} target={"_blank"}>
                  {formatLink(data.account.metadata.tripletexLink)}
                </a>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Card>
  );
});

const ProductStatus = observer(function ProductStatus({
  data,
}: {
  data: EnterpriseAccountDetails;
}) {
  const { lang } = useTranslation();
  return (
    <Card title={{ key: "common.status" }}>
      <Table variant={"IN_CARD"}>
        <colgroup>
          <col className={"w-1/2"} />
        </colgroup>
        <tbody>
          {data.subscriptionStats.map((s) => {
            const product = productLanguage(data.products, s.productId, lang);
            return (
              <tr key={s.productId}>
                <td>{product?.name}</td>
                <td>{formatAccountStatus(lang, data, s.productId)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
});

const UserView = observer(function AdminsView({
  store,
  data,
}: {
  store: EnterpriseAccountsStore;
  data: EnterpriseAccountDetails;
}) {
  const { t } = useTranslation();
  const columns: TableColumn<EnterpriseAccountUser>[] = [
    {
      name: "name",
      label: { key: "enterprise-account.admins.name" },
      getValue: (user) => (
        <Box gap={"lg"}>
          {user.name}
          {data.account.accountAdminIds.includes(user.id) && (
            <Tag color="neutral" label={t("enterprise-account.account-admin")} />
          )}
        </Box>
      ),
      align: "left",
      getSortValue: (user) => user.name,
    },
    {
      name: "role",
      label: { key: "enterprise-account.account.role" },
      getValue: (user) => {
        const role = store.usersRoles(user);
        return role ? <div>{t(role)}</div> : undefined;
      },
      align: "left",
      getSortValue: (user) => {
        const role = store.usersRoles(user);
        return role ? t(role) : undefined;
      },
    },
    {
      name: "companies",
      label: { key: "enterprise-account.admins.companies" },
      getValue: (user) => <AdminsCompaniesDisplay companies={user.companies} />,
      align: "left",
    },
    {
      name: "email",
      label: { key: "enterprise-account.admins.email" },
      getValue: (user) => user.email,
      align: "left",
      getSortValue: (user) => user.email,
    },
    {
      name: "lastLogin",
      label: { key: "enterprise-account.users.last-login" },
      getValue: (user) => <LastLogin lastLogin={user.lastSeen} />,
      align: "left",
      getSortValue: (user) => (user.lastSeen ? toDate(user.lastSeen).getTime() : 0),
    },
  ];

  return (
    <Container marginTop={"lg"} maxWidth={"xl"} gap={"lg"}>
      <h3>{t("enterprise-account.users")}</h3>
      <SearchAndActionMenu store={store} />
      <DataTable
        tableColumns={columns}
        tableData={store.filterUsers}
        getId={(u) => `${u.id}-${u.role.type === "customRole" ? u.role.roleId : "admin"}`}
        initialSortBy={"name"}
        initialSortDirection={"asc"}
        rowActions={(admin) => [
          [
            "context-menu",
            compact([
              data.account.accountAdminIds.includes(admin.id) &&
                admin.id !== store.currentUserExternalId && {
                  type: "item",
                  label: { key: "enterprise-accounts.remove-account-admin" },
                  icon: "trash",
                  onSelect: () => store.removeAccountAdmin(admin.id),
                },
              data.companyDetails.length > admin.companies.length && {
                type: "item",
                label: t("enterprise-account.add-access"),
                icon: "plus",
                onSelect: () => store.openAddAdministratorAccessDialog(admin, data.companyDetails),
              },
              admin.companies.length > 0 && {
                type: "item",
                label: t("enterprise-account.remove-access"),
                icon: "minus",
                onSelect: () => store.openRemoveAdministratorAccessDialog(admin),
              },
              store.hasSuperAdminAccess && {
                type: "item",
                label: t("enterprise-account.grant-project-access"),
                icon: "folder-user",
                onSelect: () => store.openGrantProjectAccessDialog(admin),
              },
              !data.account.accountAdminIds.includes(admin.id) && {
                type: "item",
                label: t("enterprise-account.admins.flag-admin"),
                icon: "user-check",
                onSelect: () => store.addAccountAdmin(admin.id),
              },
            ]),
          ],
        ]}
      />
    </Container>
  );
});

const SearchAndActionMenu = observer(({ store }: { store: EnterpriseAccountsStore }) => {
  const { t } = useTranslation();
  return (
    <Box justifyContent={"space-between"} vertical={isMobileView()}>
      <Box>
        <MTextInput
          placeholder={t("search")}
          icon={"magnifying-glass"}
          model={store.usersSearchModel}
        />
        {store.customRoleFilter && <MSelect model={store.customRoleFilter} />}
      </Box>
      {store.canInviteUsers && <DropdownMenu {...store.userMenuOptions} />}
    </Box>
  );
});

function AdminsCompaniesDisplay({ companies }: { companies: CompanyItem[] }) {
  const { t } = useTranslation();

  if (companies.length === 0) {
    return <div>{t("enterprise-account.no-companies")}</div>;
  }
  if (companies.length === 1) {
    return (
      <Box>
        <div>{companies[0].companyName}</div>
      </Box>
    );
  }
  return (
    <Box>
      <HoverTooltip
        content={
          <div>
            {companies.map((c) => (
              <Box key={c.organisationNumber} whitespace={"nowrap"}>
                <div>{c.companyName}</div>
              </Box>
            ))}
          </div>
        }
      >
        {({ targetRef }) => (
          <Box ref={targetRef} className={"underline decoration-dotted"}>
            {t("portfolio.numberOfCompanies", { count: companies.length })}
          </Box>
        )}
      </HoverTooltip>
    </Box>
  );
}

const AccountHistory = observer(function History({ data }: { data: EnterpriseAccountDetails }) {
  const { t, lang } = useTranslation();
  return (
    <Card title={{ key: "enterprise-account.history" }}>
      {data.eventLogs
        .map((e) => prepEventLog(e, data.products, t, lang))
        .map((eventData) => (
          <Box key={eventData.event.id} justifyContent={"space-between"}>
            <Box alignItems={"start"} gap={"md"}>
              <div>
                <Icon name={eventData.icon} />
              </div>
              <div>{eventData.date}</div>
              <VBox>
                {t(eventData.description)}
                {eventData.descriptionLines?.map((d, i) => (
                  <div key={i}>{t(d)}</div>
                ))}
              </VBox>
            </Box>
            <div>
              {eventData.description2
                ? isReactElement(eventData.description2)
                  ? eventData.description2
                  : t(eventData.description2!)
                : null}
            </div>
          </Box>
        ))}
    </Card>
  );
});

function productLanguage(products: ProductDescription[], productId: string, lang: Language) {
  return products
    .find((product) => product.productId === productId)!
    .productLanguages.find((p) => p.language === lang);
}

const prepEventLog = (
  event: EventLogItem,
  products: ProductDescription[],
  t: TFunction,
  lang: Language,
): EventLogData => {
  let icon: IconName;
  let description: TMessage;
  let descriptionLines: TMessage[] | undefined;
  let description2: ReactElement | TMessage | undefined;
  switch (event.type) {
    case "addToEnterpriseAccount":
      icon = "chart-line-up";
      description = {
        key: "enterprise-account.log.description.addToEnterpriseAccount",
        params: { name: event.user },
      };
      description2 =
        event.companies.length === 1 ? (
          { translated: `${event.companies[0].companyName} (${event.productName})` }
        ) : (
          <Box>
            <HoverTooltip
              content={
                <div>
                  {event.companies.map((c) => (
                    <div key={c.companyName}>{c.companyName}</div>
                  ))}
                </div>
              }
            >
              {({ targetRef }) => (
                <Box ref={targetRef} className={"underline decoration-dotted"}>
                  {t({
                    key: "enterprise-account.log.description2.addToEnterpriseAccount",
                    params: { count: event.companies.length },
                  })}
                </Box>
              )}
            </HoverTooltip>
            ({event.productName})
          </Box>
        );

      break;
    case "removeEnterpriseAccountItem":
      icon = "chart-line-down";
      description = {
        key: "enterprise-account.log.description.removeFromEnterpriseAccount",
        params: { name: event.user },
      };
      description2 =
        event.companies.length === 1
          ? { translated: `${event.companies[0].companyName}` }
          : {
              key: "enterprise-account.log.description2.removeFromEnterpriseAccount",
              params: { count: event.companies.length },
            };
      break;
    case "createEnterpriseAccountItem":
      icon = "handshake";
      description = {
        key: "enterprise-account.log.description.createEnterpriseAccountItem",
        params: {
          name: event.user,
          frequency: t(billingFrequencyLabels[event.billingFrequency]).toLowerCase(),
          date: event.startTime,
        },
      };
      description2 =
        event.subscriptionLimits.length === 1
          ? {
              translated: `${productLanguage(products, event.subscriptionLimits[0].productId, lang)?.name ?? ""} (${event.subscriptionLimits[0].limit})`,
            }
          : {
              key: "enterprise-account.log.description2.createEnterpriseAccountItem",
              params: { count: event.subscriptionLimits.length },
            };
      break;

    case "signingCountItem":
      icon = "signature";
      description = {
        key: "enterprise-account.log.description.signingCountItem",
        params: { count: event.numberOfSignedDocuments },
      };
      break;
    case "brSubmissionCountItem":
      icon = "dots-nine";
      description = {
        key: "enterprise-account.log.description.brSubmissionCountItem",
        params: { count: event.count },
      };
      break;
    case "invoiceItem":
      icon = "piggy-bank";
      description = {
        key: "enterprise-account.log.description.invoiceItem",
        params: { name: event.employeeName, amount: event.amount.value },
      };
      descriptionLines = event.lines.map((l) => ({
        translated: `${l.baseProductName ?? l.description ?? ""} (${l.count}) ${l.periodFrom ?? ""} - ${l.periodTo ?? ""}`,
      }));
      break;
  }

  const date = toLocalDateISOString(event.timestamp);
  return {
    event,
    icon,
    date,
    description,
    descriptionLines,
    description2,
  };
};

type EventLogData = {
  event: EventLogItem;
  icon: IconName;
  date: string;
  description: TMessage;
  descriptionLines?: TMessage[];
  description2?: TMessage | ReactElement;
};

export const BillingCycleCards = observer(({ store }: { store: EnterpriseAccountsStore }) => {
  const { t } = useTranslation();
  return (
    <>
      {store.billingFrequencyCards.map((row, j) => (
        <Grid key={j} cols={1} colsMd={calcColCount(row, 4)} gap={"lg"}>
          {row.map(({ colSpan, diffTag, ...card }, k) => {
            const extraInfo = diffTag ? (
              <Box alignItems={"center"} gap={"sm"}>
                <Tag {...diffTag} />
                <div>{t("enterprise-account.since-previous-invoice")}</div>
              </Box>
            ) : undefined;
            return (
              <GridItem key={k} md={colSpan} className={"flex"}>
                <DataCard {...(card as DataCardProps)} {...(extraInfo ? { extraInfo } : {})} />
              </GridItem>
            );
          })}
        </Grid>
      ))}
    </>
  );
});

const AddCompanyButton = observer(function AddCompanyButton({
  store,
}: {
  store: EnterpriseAccountsStore;
}) {
  const dispatch = useDispatch();
  const { lang } = useTranslation();
  const accountId = store.id;
  return (
    <DropdownMenu
      disabled={!accountId}
      alignment={"end"}
      button={{
        type: "button",
        variant: "primary",
        icon: "plus",
        label: { key: "add-company.add-company" },
      }}
      items={[
        {
          type: "item",
          icon: "plus",
          label: { key: "company-import.add-manually" },
          onSelect: () =>
            accountId &&
            dispatch(
              openAddCompany(
                accountId,
                store.accountName,
                lang,
                store.addCompanyAdminOptions,
                store.addCompanySubscriptionOptions(lang),
                store.addCompanyRegisters(lang),
                store.addCompanyParentOptions,
              ),
            ),
        },
        { type: "separator" },
        { type: "label", label: { key: "company-import.from-excel" } },
        {
          type: "item",
          icon: "download",
          label: { key: "company-import.download-template" },
          onSelect: () => {
            if (accountId) window.location.href = companyImportTemplateUrl(accountId, lang);
          },
        },
        {
          type: "file-upload",
          label: { key: "company-import.upload-file" },
          fileUploadConfig: companyImportFileConfig,
          onSelect: (files) => {
            const file = files?.[0];
            if (accountId && file) {
              dispatch(
                openCompanyImport(
                  accountId,
                  store.accountName,
                  file,
                  store.addCompanyAdminOptions,
                  store.currentUserExternalId,
                  thunk(() => store.refresh()),
                ),
              );
            }
          },
        },
      ]}
    />
  );
});

const ContentLeftPanel = observer(function ContentLeftPanel({
  templates,
}: {
  templates: TemplateState;
}) {
  const dispatch = useDispatch();
  return (
    <VBox marginX marginTop>
      <MenuGroup
        text={{ key: "enterprise-account.content.document-robot" }}
        icon={"sparkle"}
        items={projectTemplateFilterMenuItems(templates, dispatch)}
        isOpen={isTemplateTypeSelected(templates, TemplateType.PROJECT)}
      />
      {!isProductionEnvironment() && (
        <MenuGroup
          text={{ key: "enterprise-account.content.maps" }}
          icon={"treeStructure"}
          iconRotate={90}
          items={mapTemplateFilterMenuItems(templates, dispatch)}
          isOpen={isTemplateTypeSelected(templates, TemplateType.MAP)}
        />
      )}
    </VBox>
  );
});
